import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ArrowBack, Category } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { costsApi } from '../../services/benchmarkApi';
import { formatMoney, projectLabel } from './benchmarkFormat';

const RootDiv = styled('div')({
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ContentDiv = styled('div')({
  flexGrow: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: '0',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 120px)',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  margin: theme.spacing(1),
}));

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const TitleBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2),
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
}));

const Title = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
  '& svg': {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const money = (v) => formatMoney(v);

/**
 * Cost Category = live structure cost roll-up from uploaded Estimate/CO/Actual
 * (no hardcoded mock rows).
 */
const BenchmarkCostCategory = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const location = useLocation();
  const project = location.state?.project;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [structureType, setStructureType] = useState('CBS');

  const loadRows = useCallback(async () => {
    if (!projectId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await costsApi.structureCost(projectId, structureType);
      const structureRows = res.data?.structureRows || res.data?.rows || [];
      setRows(
        structureRows.map((r, idx) => ({
          id: r.structureNodeId || r.structureCode || idx,
          code: r.structureCode || '',
          categoryDescription: r.structureName || r.structureCode || '',
          originalCost: r.originalCost,
          changeOrders: r.changeOrders,
          revisedCost: r.revisedCost,
          actualCost: r.actualCost,
          variancePercent: r.variancePercent,
        }))
      );
    } catch (e) {
      setError(e.message || 'Failed to load cost categories');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, structureType]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const columns = [
    { field: 'code', headerName: 'Code', width: 140 },
    {
      field: 'categoryDescription',
      headerName: 'Category / Structure',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'originalCost',
      headerName: 'Estimate / BoQ',
      width: 140,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'changeOrders',
      headerName: 'CO',
      width: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'revisedCost',
      headerName: 'Revised',
      width: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'actualCost',
      headerName: 'Actual',
      width: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'variancePercent',
      headerName: 'Var %',
      width: 90,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) =>
        v == null || v === '' ? '—' : `${Number(v).toFixed(2)}%`,
    },
  ];

  return (
    <RootDiv>
      <ContentDiv>
        <StyledPaper>
          <HeaderContainer>
            <TitleBar>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  size="small"
                  onClick={() => navigate(`/benchmark/contracts/${projectId}`, { state: { project } })}
                >
                  <ArrowBack />
                </IconButton>
                <Title variant="h6">
                  <Category fontSize="small" />
                  Cost Category
                  {project?.projectName || project?.projectNumber
                    ? ` — ${projectLabel(project)}`
                    : ''}
                </Title>
              </Box>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Structure type</InputLabel>
                <Select
                  label="Structure type"
                  value={structureType}
                  onChange={(e) => setStructureType(e.target.value)}
                >
                  {['CBS', 'WBS', 'MASTERFORMAT', 'UNIFORMAT', 'POMI'].map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </TitleBar>
          </HeaderContainer>

          {error && (
            <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box sx={{ flex: 1, px: 2, pb: 2, position: 'relative' }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!loading && rows.length === 0 && (
              <Alert severity="info" sx={{ mb: 1 }}>
                No structure costs yet. Upload Estimate / CO / Actual for this project — categories
                appear from structure codes on those lines.
              </Alert>
            )}
            <DataGrid
              rows={rows}
              columns={columns}
              density="compact"
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              sx={{ border: 'none', height: 'calc(100% - 8px)' }}
            />
          </Box>
        </StyledPaper>
      </ContentDiv>
    </RootDiv>
  );
};

export default BenchmarkCostCategory;
